import { axiosInstance as api } from "./api";
import type {
  Candidate,
  Job,
  Application,
  Customer,
  Stage,
  User,
  UserRole,
  StageLevel,
} from "../types/shared.types";

// Type definitions for API responses and request payloads
interface BulkOperationResult {
  success: boolean;
  updated?: number;
  deleted?: number;
  message?: string;
}

interface SkillRecommendation {
  skill: string;
  current: number;
  target: number;
  priority: "high" | "medium" | "low";
  description: string;
}

interface CandidateInfo {
  skills?: string[];
  experience?: string;
  education?: string;
  // Additional fields used by skill recommendations
  candidateName?: string;
  currentRole?: string;
  experienceLevel?: string;
  location?: string;
  mainSkill?: string;
  careerGoals?: string;
  currentPosition?: string;
  tags?: string;
  summary?: string;
}

interface FindCandidatesOptions {
  page?: number;
  pageSize?: number;
  [key: string]: unknown;
}

interface CreateUserPayload extends Partial<User> {
  password: string;
}

interface AIGenerationOptions {
  [key: string]: unknown;
}

interface TechCheck {
  id?: number;
  candidate_id: number;
  status?: string;
  notes?: string;
  proposals?: unknown[];
  [key: string]: unknown;
}

interface Note {
  id?: number;
  entity_type: string;
  entity_id: number;
  content: string;
  created_by?: number;
  created_at?: string;
  [key: string]: unknown;
}

interface Assessment {
  id?: number;
  candidate_id: number;
  job_id?: number;
  score?: number;
  summary?: string;
  [key: string]: unknown;
}

interface CVGenerationOptions {
  format?: string;
  template?: string;
  [key: string]: unknown;
}

interface SemanticSearchResult {
  success?: boolean;
  data: Array<{
    id: number;
    similarity_score: number;
    [key: string]: unknown;
  }>;
  unindexed?: Array<{
    id: number;
    [key: string]: unknown;
  }>;
  total?: number;
  message?: string;
}

// Recruiting API service layer
export const recruitingAPI = {
  // Candidates
  createCandidate: async (data: Partial<Candidate>): Promise<Candidate> => {
    try {
      const response = await api.post("/recruiting/candidates", data);
      return response.data;
    } catch (error) {
      console.error("Error creating candidate:", error);
      throw error;
    }
  },

  getCandidates: async (
    filters: Record<string, unknown> = {},
  ): Promise<Candidate[]> => {
    try {
      const response = await api.get("/recruiting/candidates", {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error("Error getting candidates:", error);
      throw error;
    }
  },

  getCandidateFilterMetadata: async (): Promise<{
    data: { skills: string[]; sources: string[]; seniorities: string[] };
  }> => {
    const response = await api.get("/recruiting/candidates/filters/metadata");
    return response.data;
  },

  getCandidate: async (id: number | string): Promise<Candidate> => {
    try {
      const response = await api.get(`/recruiting/candidates/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error getting candidate:", error);
      throw error;
    }
  },

  updateCandidate: async (
    id: number | string,
    data: Partial<Candidate>,
  ): Promise<Candidate> => {
    try {
      const response = await api.put(`/recruiting/candidates/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating candidate:", error);
      throw error;
    }
  },

  deleteCandidate: async (
    id: number | string,
  ): Promise<BulkOperationResult> => {
    try {
      const response = await api.delete(`/recruiting/candidates/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting candidate:", error);
      throw error;
    }
  },

  // Bulk Candidate Operations
  bulkUpdateCandidates: async (
    ids: number[],
    updateData: Partial<Candidate>,
  ): Promise<BulkOperationResult> => {
    try {
      const response = await api.put("/recruiting/candidates/bulk", {
        ids,
        updateData,
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk updating candidates:", error);
      throw error;
    }
  },

  bulkDeleteCandidates: async (ids: number[]): Promise<BulkOperationResult> => {
    try {
      const response = await api.delete("/recruiting/candidates/bulk", {
        data: { ids },
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk deleting candidates:", error);
      throw error;
    }
  },

  // Candidate Skills
  getCandidateSkills: async (id: number | string): Promise<unknown> => {
    try {
      const response = await api.get(`/recruiting/candidates/${id}/skills`);
      return response.data;
    } catch (error) {
      console.error("Error getting candidate skills:", error);
      throw error;
    }
  },

  updateCandidateSkills: async (
    id: number | string,
    skills: unknown,
  ): Promise<unknown> => {
    try {
      const response = await api.put(`/recruiting/candidates/${id}/skills`, {
        skills,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating candidate skills:", error);
      throw error;
    }
  },

  // AI Skills Assessment
  getSkillRecommendations: async (
    candidateId: number | string,
    roleType: string = "DEVELOPER",
    candidateInfo: CandidateInfo = {},
  ): Promise<SkillRecommendation[]> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/skills/recommendations`,
        {
          roleType,
          candidateInfo,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error getting skill recommendations:", error);
      throw error;
    }
  },

  getSkillGapAnalysis: async (
    candidateId: number | string,
    targetRole: string,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/skills/gap-analysis`,
        {
          targetRole,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error getting skill gap analysis:", error);
      throw error;
    }
  },

  getLearningPlan: async (
    candidateId: number | string,
    careerGoal: string,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/skills/learning-plan`,
        {
          careerGoal,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error getting learning plan:", error);
      throw error;
    }
  },

  // Jobs
  createJob: async (data: Partial<Job>): Promise<Job> => {
    try {
      const response = await api.post("/recruiting/jobs", data);
      return response.data;
    } catch (error) {
      console.error("Error creating job:", error);
      throw error;
    }
  },

  getJobs: async (filters: Record<string, unknown> = {}): Promise<Job[]> => {
    try {
      const response = await api.get("/recruiting/jobs", { params: filters });
      return response.data;
    } catch (error) {
      console.error("Error getting jobs:", error);
      throw error;
    }
  },

  getJob: async (id: number | string): Promise<Job> => {
    try {
      const response = await api.get(`/recruiting/jobs/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error getting job:", error);
      throw error;
    }
  },

  updateJob: async (id: number | string, data: Partial<Job>): Promise<Job> => {
    try {
      const response = await api.put(`/recruiting/jobs/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating job:", error);
      throw error;
    }
  },

  deleteJob: async (id: number | string): Promise<BulkOperationResult> => {
    try {
      const response = await api.delete(`/recruiting/jobs/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting job:", error);
      throw error;
    }
  },

  assignRecruiterToJob: async (
    jobId: number | string,
    recruiterId: number,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/jobs/${jobId}/assign-recruiter`,
        { recruiterId },
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning recruiter to job:", error);
      throw error;
    }
  },

  removeRecruiterFromJob: async (
    jobId: number | string,
    recruiterId: number,
  ): Promise<unknown> => {
    try {
      const response = await api.delete(
        `/recruiting/jobs/${jobId}/assign-recruiter/${recruiterId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error removing recruiter from job:", error);
      throw error;
    }
  },

  // Bulk Job Operations
  bulkUpdateJobs: async (
    ids: number[],
    updateData: Partial<Job>,
  ): Promise<BulkOperationResult> => {
    try {
      const response = await api.put("/recruiting/jobs/bulk", {
        ids,
        updateData,
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk updating jobs:", error);
      throw error;
    }
  },

  bulkDeleteJobs: async (ids: number[]): Promise<BulkOperationResult> => {
    try {
      const response = await api.delete("/recruiting/jobs/bulk", {
        data: { ids },
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk deleting jobs:", error);
      throw error;
    }
  },

  // Job AI Features
  extendJobDescription: async (
    jobId: number | string,
    options: AIGenerationOptions = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/jobs/${jobId}/extend-description`,
        { options },
      );
      return response.data;
    } catch (error) {
      console.error("Error extending job description:", error);
      throw error;
    }
  },

  findCandidatesForJob: async (
    jobId: number | string,
    options: FindCandidatesOptions = {},
  ): Promise<unknown> => {
    try {
      // Send options and pagination directly in body
      const response = await api.post(
        `/recruiting/jobs/${jobId}/find-candidates`,
        {
          options: options,
          page: options.page,
          pageSize: options.pageSize,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error finding candidates for job:", error);
      throw error;
    }
  },

  generateLinkedInPost: async (
    jobId: number | string,
    options: AIGenerationOptions = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/jobs/${jobId}/generate-linkedin-post`,
        { options },
      );
      return response.data;
    } catch (error) {
      console.error("Error generating LinkedIn post:", error);
      throw error;
    }
  },

  searchApolloForJob: async (
    jobId: number | string,
    options: AIGenerationOptions = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/jobs/${jobId}/search-apollo`,
        { options },
      );
      return response.data;
    } catch (error) {
      console.error("Error searching Apollo for job:", error);
      throw error;
    }
  },

  // Customers
  createCustomer: async (data: Partial<Customer>): Promise<Customer> => {
    try {
      const response = await api.post("/recruiting/customers", data);
      return response.data;
    } catch (error) {
      console.error("Error creating customer:", error);
      throw error;
    }
  },

  getCustomers: async (
    filters: Record<string, unknown> = {},
  ): Promise<Customer[]> => {
    try {
      const response = await api.get("/recruiting/customers", {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error("Error getting customers:", error);
      throw error;
    }
  },

  getCustomer: async (id: number | string): Promise<Customer> => {
    try {
      const response = await api.get(`/recruiting/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error getting customer:", error);
      throw error;
    }
  },

  updateCustomer: async (
    id: number | string,
    data: Partial<Customer>,
  ): Promise<Customer> => {
    try {
      const response = await api.put(`/recruiting/customers/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating customer:", error);
      throw error;
    }
  },

  deleteCustomer: async (id: number | string): Promise<BulkOperationResult> => {
    try {
      const response = await api.delete(`/recruiting/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting customer:", error);
      throw error;
    }
  },

  // Bulk Customer Operations
  bulkUpdateCustomers: async (
    ids: number[],
    updateData: Partial<Customer>,
  ): Promise<BulkOperationResult> => {
    try {
      const response = await api.put("/recruiting/customers/bulk", {
        ids,
        updateData,
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk updating customers:", error);
      throw error;
    }
  },

  bulkDeleteCustomers: async (ids: number[]): Promise<BulkOperationResult> => {
    try {
      const response = await api.delete("/recruiting/customers/bulk", {
        data: { ids },
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk deleting customers:", error);
      throw error;
    }
  },

  // System Config
  getSystemConfig: async (key?: string): Promise<unknown> => {
    try {
      const response = await api.get("/recruiting/system-config", {
        params: key ? { key } : {},
      });
      return response.data;
    } catch (error) {
      console.error("Error getting system config:", error);
      throw error;
    }
  },

  setSystemConfig: async (key: string, value: unknown): Promise<unknown> => {
    try {
      const response = await api.post("/recruiting/system-config", {
        key,
        value,
      });
      return response.data;
    } catch (error) {
      console.error("Error setting system config:", error);
      throw error;
    }
  },

  // Admin user management
  getAdminUsers: async (): Promise<User[]> => {
    try {
      const response = await api.get("/admin/users");
      return response.data;
    } catch (error) {
      console.error("Error getting admin users:", error);
      throw error;
    }
  },

  createUser: async (userData: CreateUserPayload): Promise<User> => {
    try {
      const response = await api.post("/admin/users", userData);
      return response.data;
    } catch (error) {
      console.error("Error creating user:", error);
      throw error;
    }
  },

  updateUser: async (
    userId: number | string,
    data: Partial<User>,
  ): Promise<User> => {
    try {
      const response = await api.put(`/admin/users/${userId}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  },

  deleteUser: async (userId: number | string): Promise<BulkOperationResult> => {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  },

  getUsersByRole: async (role: UserRole): Promise<User[]> => {
    try {
      const response = await api.get(`/recruiting/users/by-role/${role}`);
      return response.data;
    } catch (error) {
      console.error("Error getting users by role:", error);
      throw error;
    }
  },

  // Applications
  createApplication: async (
    data: Partial<Application>,
  ): Promise<Application & { warning?: string }> => {
    try {
      const response = await api.post("/recruiting/applications", data);
      const result = response.data?.data || response.data;
      if (response.data?.warning) {
        return { ...result, warning: response.data.warning };
      }
      return result;
    } catch (error) {
      console.error("Error creating application:", error);
      throw error;
    }
  },

  getApplications: async (
    filters: Record<string, unknown> = {},
  ): Promise<Application[]> => {
    try {
      const response = await api.get("/recruiting/applications", {
        params: filters,
      });
      const body = response.data;
      return Array.isArray(body?.data) ? body.data : body;
    } catch (error) {
      console.error("Error getting applications:", error);
      throw error;
    }
  },

  getApplication: async (id: number | string): Promise<Application> => {
    try {
      const response = await api.get(`/recruiting/applications/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error getting application:", error);
      throw error;
    }
  },

  updateApplication: async (
    id: number | string,
    data: Partial<Application>,
  ): Promise<Application> => {
    try {
      const response = await api.put(`/recruiting/applications/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating application:", error);
      throw error;
    }
  },

  deleteApplication: async (
    id: number | string,
    options?: { reason?: string; note?: string },
  ): Promise<BulkOperationResult> => {
    try {
      const response = await api.delete(`/recruiting/applications/${id}`, {
        data: options,
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting application:", error);
      throw error;
    }
  },

  // Bulk Application Operations
  bulkUpdateApplications: async (
    ids: number[],
    updateData: Partial<Application>,
  ): Promise<BulkOperationResult> => {
    try {
      const response = await api.put("/recruiting/applications/bulk", {
        ids,
        updateData,
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk updating applications:", error);
      throw error;
    }
  },

  bulkDeleteApplications: async (
    ids: number[],
  ): Promise<BulkOperationResult> => {
    try {
      const response = await api.delete("/recruiting/applications/bulk", {
        data: { ids },
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk deleting applications:", error);
      throw error;
    }
  },

  // Application Status Workflow
  updateApplicationStatus: async (
    id: number | string,
    status: string,
    options?: { note?: string; override_justification?: string },
  ): Promise<Application & { _metadata?: Record<string, unknown> }> => {
    try {
      const response = await api.patch(
        `/recruiting/applications/${id}/status`,
        {
          status,
          ...options,
        },
      );
      // Return data + _metadata if present (lifecycle coordination)
      const result = response.data?.data || response.data;
      if (response.data?._metadata) {
        return { ...result, _metadata: response.data._metadata };
      }
      return result;
    } catch (error) {
      console.error("Error updating application status:", error);
      throw error;
    }
  },

  getApplicationAllowedTransitions: async (
    id: number | string,
  ): Promise<{
    current_status: string;
    transitions: Array<{
      value: string;
      label: string;
      color: string;
      warnings?: string[];
      requiresJustification?: boolean;
    }>;
    is_terminal: boolean;
  }> => {
    try {
      const response = await api.get(
        `/recruiting/applications/${id}/allowed-transitions`,
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("Error getting allowed transitions:", error);
      throw error;
    }
  },

  rejectApplication: async (
    id: number | string,
    rejectionReason: string,
    options?: { rejection_reason_note?: string; note?: string },
  ): Promise<Application> => {
    try {
      const response = await api.post(`/recruiting/applications/${id}/reject`, {
        rejection_reason: rejectionReason,
        ...options,
      });
      return response.data?.data || response.data;
    } catch (error) {
      console.error("Error rejecting application:", error);
      throw error;
    }
  },

  bulkUpdateApplicationStatus: async (
    ids: number[],
    status: string,
    options?: {
      note?: string;
      rejection_reason?: string;
      rejection_reason_note?: string;
      override_justification?: string;
    },
  ): Promise<BulkOperationResult> => {
    try {
      const response = await api.patch("/recruiting/applications/bulk/status", {
        ids,
        status,
        ...options,
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk updating application status:", error);
      throw error;
    }
  },

  getApplicationStatusHistory: async (
    id: number | string,
  ): Promise<unknown[]> => {
    try {
      const response = await api.get(
        `/recruiting/applications/${id}/status-history`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting application status history:", error);
      throw error;
    }
  },

  moveJobStage: async (
    id: number | string,
    toStageId: number,
    note?: string,
  ): Promise<Job> => {
    try {
      const response = await api.patch(`/recruiting/jobs/${id}/stage`, {
        toStageId,
        note,
      });
      return response.data;
    } catch (error) {
      console.error("Error moving job stage:", error);
      throw error;
    }
  },

  /** @deprecated Use getApplicationStatusHistory instead */
  getApplicationHistory: async (id: number | string): Promise<unknown[]> => {
    try {
      const response = await api.get(
        `/recruiting/applications/${id}/status-history`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting application history:", error);
      throw error;
    }
  },

  getJobActivityHistory: async (
    id: number | string,
    options?: {
      limit?: number;
      offset?: number;
      eventTypes?: string[];
      startDate?: string;
      endDate?: string;
    },
  ): Promise<unknown> => {
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.append("limit", String(options.limit));
      if (options?.offset) params.append("offset", String(options.offset));
      if (options?.eventTypes?.length)
        params.append("eventTypes", options.eventTypes.join(","));
      if (options?.startDate) params.append("startDate", options.startDate);
      if (options?.endDate) params.append("endDate", options.endDate);

      const queryString = params.toString();
      const url = `/recruiting/jobs/${id}/activity-history${queryString ? `?${queryString}` : ""}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("Error getting job activity history:", error);
      throw error;
    }
  },

  getJobActivitySummary: async (id: number | string): Promise<unknown> => {
    try {
      const response = await api.get(`/recruiting/jobs/${id}/activity-summary`);
      return response.data;
    } catch (error) {
      console.error("Error getting job activity summary:", error);
      throw error;
    }
  },

  getCandidateHistory: async (id: number | string): Promise<unknown[]> => {
    try {
      const response = await api.get(`/recruiting/candidates/${id}/history`);
      return response.data;
    } catch (error) {
      console.error("Error getting candidate history:", error);
      throw error;
    }
  },

  // Stages
  // Note: Backend wraps response in { success, data } envelope.
  // We unwrap here so consumers receive Stage[] directly.
  getStages: async (level: StageLevel | null = null): Promise<Stage[]> => {
    try {
      const params = level ? { level } : {};
      const response = await api.get("/recruiting/stages", { params });
      const stages = response.data?.data;
      if (response.data?.success && !Array.isArray(stages)) {
        console.error(
          "getStages: unexpected response shape — 'data' is not an array",
          {
            level,
            responseKeys: Object.keys(response.data || {}),
          },
        );
      }
      return Array.isArray(stages) ? stages : [];
    } catch (error) {
      console.error("Error getting stages:", error);
      throw error;
    }
  },

  createStage: async (data: Partial<Stage>): Promise<Stage> => {
    try {
      const response = await api.post("/recruiting/stages", data);
      return response.data;
    } catch (error) {
      console.error("Error creating stage:", error);
      throw error;
    }
  },

  updateStage: async (
    id: number | string,
    data: Partial<Stage>,
  ): Promise<Stage> => {
    try {
      const response = await api.put(`/recruiting/stages/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating stage:", error);
      throw error;
    }
  },

  deleteStage: async (id: number | string): Promise<BulkOperationResult> => {
    try {
      const response = await api.delete(`/recruiting/stages/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting stage:", error);
      throw error;
    }
  },

  reorderStages: async (
    level: StageLevel,
    newOrder: number[],
  ): Promise<unknown> => {
    try {
      const response = await api.post("/recruiting/stages/reorder", {
        level,
        newOrder,
      });
      return response.data;
    } catch (error) {
      console.error("Error reordering stages:", error);
      throw error;
    }
  },

  // Tech Check
  getTechCheck: async (candidateId: number | string): Promise<TechCheck> => {
    try {
      const response = await api.get(
        `/recruiting/candidates/${candidateId}/tech-check`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting tech check:", error);
      throw error;
    }
  },

  updateTechCheck: async (
    candidateId: number | string,
    data: Partial<TechCheck>,
  ): Promise<TechCheck> => {
    try {
      const response = await api.put(
        `/recruiting/candidates/${candidateId}/tech-check`,
        data,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating tech check:", error);
      throw error;
    }
  },

  generateTechCheckProposals: async (
    candidateId: number | string,
    jobId: number | string,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/tech-check/generate`,
        {
          jobId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error generating tech check proposals:", error);
      throw error;
    }
  },

  // Tech Checker Assignments
  getTechCheckersForCandidate: async (
    candidateId: number | string,
  ): Promise<User[]> => {
    try {
      const response = await api.get(
        `/recruiting/candidates/${candidateId}/tech-checkers`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting tech checkers:", error);
      throw error;
    }
  },

  assignTechCheckerToCandidate: async (
    candidateId: number | string,
    techCheckerId: number,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/assign-tech-checker`,
        {
          techCheckerId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning tech checker:", error);
      throw error;
    }
  },

  removeTechCheckerFromCandidate: async (
    candidateId: number | string,
    techCheckerId: number,
  ): Promise<unknown> => {
    try {
      const response = await api.delete(
        `/recruiting/candidates/${candidateId}/assign-tech-checker/${techCheckerId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error removing tech checker:", error);
      throw error;
    }
  },

  // Candidate Recruiter Assignments
  getRecruitersForCandidate: async (
    candidateId: number | string,
  ): Promise<User[]> => {
    try {
      const response = await api.get(
        `/recruiting/candidates/${candidateId}/recruiters`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting candidate recruiters:", error);
      throw error;
    }
  },

  assignRecruiterToCandidate: async (
    candidateId: number | string,
    recruiterId: number,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/assign-recruiter`,
        {
          recruiterId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning recruiter to candidate:", error);
      throw error;
    }
  },

  removeRecruiterFromCandidate: async (
    candidateId: number | string,
    recruiterId: number,
  ): Promise<unknown> => {
    try {
      const response = await api.delete(
        `/recruiting/candidates/${candidateId}/assign-recruiter/${recruiterId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error removing recruiter from candidate:", error);
      throw error;
    }
  },

  // Application KAM Assignments (Feature #5352)
  getKamsForApplication: async (
    applicationId: number | string,
  ): Promise<unknown> => {
    try {
      const response = await api.get(
        `/recruiting/applications/${applicationId}/kams`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting application KAMs:", error);
      throw error;
    }
  },

  assignKamToApplication: async (
    applicationId: number | string,
    kamUserId: number,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/applications/${applicationId}/assign-kam`,
        {
          kamUserId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning KAM to application:", error);
      throw error;
    }
  },

  removeKamFromApplication: async (
    applicationId: number | string,
    kamUserId: number,
  ): Promise<unknown> => {
    try {
      const response = await api.delete(
        `/recruiting/applications/${applicationId}/assign-kam/${kamUserId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error removing KAM from application:", error);
      throw error;
    }
  },

  // Candidate KAM Assignments (Feature #5352)
  getKamsForCandidate: async (
    candidateId: number | string,
  ): Promise<unknown> => {
    try {
      const response = await api.get(
        `/recruiting/candidates/${candidateId}/kams`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting candidate KAMs:", error);
      throw error;
    }
  },

  assignKamToCandidate: async (
    candidateId: number | string,
    kamUserId: number,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/assign-kam`,
        {
          kamUserId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning KAM to candidate:", error);
      throw error;
    }
  },

  removeKamFromCandidate: async (
    candidateId: number | string,
    kamUserId: number,
  ): Promise<unknown> => {
    try {
      const response = await api.delete(
        `/recruiting/candidates/${candidateId}/assign-kam/${kamUserId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error removing KAM from candidate:", error);
      throw error;
    }
  },

  // ===== Candidate Status Workflow (Feature #4936) =====

  // Update candidate status with validation
  updateCandidateStatus: async (
    candidateId: number | string,
    statusData: Record<string, unknown>,
  ): Promise<Candidate> => {
    try {
      const response = await api.patch(
        `/recruiting/candidates/${candidateId}/status`,
        statusData,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating candidate status:", error);
      throw error;
    }
  },

  // Get status change history for a candidate
  getCandidateStatusHistory: async (
    candidateId: number | string,
  ): Promise<unknown[]> => {
    try {
      const response = await api.get(
        `/recruiting/candidates/${candidateId}/status-history`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting status history:", error);
      throw error;
    }
  },

  // Get allowed status transitions for current status
  getAllowedTransitions: async (
    candidateId: number | string,
  ): Promise<unknown> => {
    try {
      const response = await api.get(
        `/recruiting/candidates/${candidateId}/allowed-transitions`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting allowed transitions:", error);
      throw error;
    }
  },

  // Archive candidate with reason
  archiveCandidate: async (
    candidateId: number | string,
    archiveData: Record<string, unknown>,
  ): Promise<Candidate> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/archive`,
        archiveData,
      );
      return response.data;
    } catch (error) {
      console.error("Error archiving candidate:", error);
      throw error;
    }
  },

  // Link candidate as duplicate of another profile
  linkDuplicateCandidate: async (
    candidateId: number | string,
    duplicateOfId: number,
  ): Promise<Candidate> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/link-duplicate`,
        {
          duplicate_of_id: duplicateOfId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error linking duplicate:", error);
      throw error;
    }
  },

  // Toggle open for more projects flag (User Story #4939)
  setCandidateOpenForProjects: async (
    candidateId: number | string,
    isOpen: boolean,
  ): Promise<Candidate> => {
    try {
      const response = await api.patch(
        `/recruiting/candidates/${candidateId}/open-for-projects`,
        {
          open_for_more_projects: isOpen,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error setting open for projects:", error);
      throw error;
    }
  },

  // Get status configuration (statuses, colors, transitions)
  getStatusConfig: async (): Promise<unknown> => {
    try {
      const response = await api.get("/recruiting/candidates/status-config");
      return response.data;
    } catch (error) {
      console.error("Error getting status config:", error);
      throw error;
    }
  },

  // ===== End Candidate Status Workflow =====

  // ===== Job Status Workflow (Feature #4940) =====

  // Update job status with validation
  updateJobStatus: async (
    jobId: number | string,
    statusData: Record<string, unknown>,
  ): Promise<Job> => {
    try {
      const response = await api.patch(
        `/recruiting/jobs/${jobId}/status`,
        statusData,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating job status:", error);
      throw error;
    }
  },

  // Get status change history for a job
  getJobStatusHistory: async (jobId: number | string): Promise<unknown[]> => {
    try {
      const response = await api.get(
        `/recruiting/jobs/${jobId}/status-history`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting job status history:", error);
      throw error;
    }
  },

  // Get allowed status transitions for current job status
  getAllowedJobTransitions: async (
    jobId: number | string,
  ): Promise<unknown> => {
    try {
      const response = await api.get(
        `/recruiting/jobs/${jobId}/allowed-transitions`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting allowed job transitions:", error);
      throw error;
    }
  },

  // Archive job with reason
  archiveJob: async (
    jobId: number | string,
    archiveData: Record<string, unknown>,
  ): Promise<Job> => {
    try {
      const response = await api.post(
        `/recruiting/jobs/${jobId}/archive`,
        archiveData,
      );
      return response.data;
    } catch (error) {
      console.error("Error archiving job:", error);
      throw error;
    }
  },

  // ===== End Job Status Workflow =====

  // ===== Admin Transition Rules =====

  getTransitionRules: async (entityType: "job" | "candidate") => {
    try {
      const response = await api.get(`/admin/transition-rules/${entityType}`);
      return response.data;
    } catch (error) {
      console.error("Error getting transition rules:", error);
      throw error;
    }
  },

  updateTransitionRules: async (
    entityType: "job" | "candidate",
    disabledTransitions: Record<string, string[]>,
  ) => {
    try {
      const response = await api.put(`/admin/transition-rules/${entityType}`, {
        disabled_transitions: disabledTransitions,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating transition rules:", error);
      throw error;
    }
  },

  resetTransitionRules: async (entityType: "job" | "candidate") => {
    try {
      const response = await api.post(
        `/admin/transition-rules/${entityType}/reset`,
      );
      return response.data;
    } catch (error) {
      console.error("Error resetting transition rules:", error);
      throw error;
    }
  },

  // ===== End Admin Transition Rules =====

  // Notes
  createNote: async (data: Partial<Note>): Promise<Note> => {
    try {
      const response = await api.post("/recruiting/notes", data);
      return response.data;
    } catch (error) {
      console.error("Error creating note:", error);
      throw error;
    }
  },

  getNotes: async (
    entityType: string,
    entityId: number | string,
  ): Promise<Note[]> => {
    try {
      const response = await api.get("/recruiting/notes", {
        params: { entity_type: entityType, entity_id: entityId },
      });
      return response.data;
    } catch (error) {
      console.error("Error getting notes:", error);
      throw error;
    }
  },

  updateNote: async (
    noteId: number | string,
    data: Partial<Note>,
  ): Promise<Note> => {
    try {
      const response = await api.put(`/recruiting/notes/${noteId}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating note:", error);
      throw error;
    }
  },

  deleteNote: async (noteId: number | string): Promise<BulkOperationResult> => {
    try {
      const response = await api.delete(`/recruiting/notes/${noteId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting note:", error);
      throw error;
    }
  },

  // Bulk Note Operations
  bulkUpdateNotes: async (
    ids: number[],
    updateData: Partial<Note>,
  ): Promise<BulkOperationResult> => {
    try {
      const response = await api.put("/recruiting/notes/bulk", {
        ids,
        updateData,
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk updating notes:", error);
      throw error;
    }
  },

  bulkDeleteNotes: async (ids: number[]): Promise<BulkOperationResult> => {
    try {
      const response = await api.delete("/recruiting/notes/bulk", {
        data: { ids },
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk deleting notes:", error);
      throw error;
    }
  },

  // AI Search
  semanticSearch: async (
    query: string,
    topK: number = 20,
    filters: Record<string, string> = {},
  ): Promise<SemanticSearchResult> => {
    try {
      const response = await api.post("/recruiting/search/semantic", {
        query,
        topK,
        filters,
      });
      return response.data;
    } catch (error) {
      console.error("Error performing semantic search:", error);
      throw error;
    }
  },

  semanticSearchJobs: async (
    query: string,
    topK: number = 20,
    filters: Record<string, string> = {},
  ): Promise<SemanticSearchResult> => {
    try {
      const response = await api.post("/recruiting/search/jobs/semantic", {
        query,
        topK,
        filters,
      });
      return response.data;
    } catch (error) {
      console.error("Error performing semantic search on jobs:", error);
      throw error;
    }
  },

  summarizeCandidate: async (id: number | string): Promise<unknown> => {
    try {
      const response = await api.post(`/recruiting/candidates/${id}/summarize`);
      return response.data;
    } catch (error) {
      console.error("Error generating candidate summary:", error);
      throw error;
    }
  },

  indexCandidate: async (id: number | string): Promise<unknown> => {
    try {
      const response = await api.post(`/recruiting/candidates/${id}/index`);
      return response.data;
    } catch (error) {
      console.error("Error indexing candidate:", error);
      throw error;
    }
  },

  // CV Generation
  generateCV: async (
    candidateId: number | string,
    options: CVGenerationOptions = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/generate-cv`,
        options,
      );
      return response.data;
    } catch (error) {
      console.error("Error generating CV:", error);
      throw error;
    }
  },

  editCV: async (
    candidateId: number | string,
    prompt: string,
    currentCV: string,
    options: CVGenerationOptions = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/edit-cv`,
        {
          prompt,
          currentCV,
          options,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error editing CV:", error);
      throw error;
    }
  },

  getCVPreview: async (candidateId: number | string): Promise<unknown> => {
    try {
      const response = await api.get(
        `/recruiting/candidates/${candidateId}/cv-preview`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting CV preview:", error);
      throw error;
    }
  },

  // Candidate Assessment
  assessCandidateForRole: async (
    candidateId: number | string,
    jobId: number | string,
    options: AIGenerationOptions = {},
  ): Promise<Assessment> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/assess/${jobId}`,
        { options },
      );
      return response.data;
    } catch (error) {
      console.error("Error assessing candidate for role:", error);
      throw error;
    }
  },

  compareCandidatesForRole: async (
    candidateIds: number[],
    jobId: number | string,
    options: AIGenerationOptions = {},
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/compare/${jobId}`,
        {
          candidateIds,
          options,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error comparing candidates for role:", error);
      throw error;
    }
  },

  getCandidateAssessments: async (
    candidateId: number | string,
  ): Promise<Assessment[]> => {
    try {
      const response = await api.get(
        `/recruiting/candidates/${candidateId}/assessments`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting candidate assessments:", error);
      throw error;
    }
  },

  getJobAssessments: async (jobId: number | string): Promise<Assessment[]> => {
    try {
      const response = await api.get(`/recruiting/jobs/${jobId}/assessments`);
      return response.data;
    } catch (error) {
      console.error("Error getting job assessments:", error);
      throw error;
    }
  },

  // Generate general technical skills assessment with top 5 skills
  generateTechnicalSkillsAssessment: async (
    candidateId: number | string,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/technical-skills-assessment`,
      );
      return response.data;
    } catch (error) {
      console.error("Error generating technical skills assessment:", error);
      throw error;
    }
  },

  // AI Summary
  generateSummary: async (userId: number | string): Promise<unknown> => {
    try {
      const response = await api.post("/recruiting/agents/generate-summary", {
        userId,
      });
      return response.data;
    } catch (error) {
      console.error("Error generating AI summary:", error);
      throw error;
    }
  },

  // Job-Specific CV Generation
  generateJobSpecificCV: async (
    applicationId: number | string,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/applications/${applicationId}/generate-cv`,
      );
      return response.data;
    } catch (error) {
      console.error("Error generating job-specific CV:", error);
      throw error;
    }
  },

  // Skills Search (for autocomplete)
  searchSkills: async (query: string): Promise<string[]> => {
    try {
      const response = await api.get("/recruiting/skills/search", {
        params: { q: query },
      });
      return response.data;
    } catch (error) {
      console.error("Error searching skills:", error);
      throw error;
    }
  },

  // ===== GDPR Consent Management (Feature #4943) =====

  // Get GDPR status for a candidate
  getCandidateGdpr: async (candidateId: number | string): Promise<unknown> => {
    try {
      const response = await api.get(
        `/recruiting/candidates/${candidateId}/gdpr`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting candidate GDPR status:", error);
      throw error;
    }
  },

  // Request GDPR consent from candidate
  requestGdprConsent: async (
    candidateId: number | string,
    consentType: "internal" | "external" = "internal",
    externalUrl?: string,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/gdpr/request`,
        {
          consent_type: consentType,
          external_form_url: externalUrl,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error requesting GDPR consent:", error);
      throw error;
    }
  },

  // Record external consent for a candidate
  recordExternalGdprConsent: async (
    candidateId: number | string,
    externalFormUrl: string,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/gdpr/external`,
        { external_form_url: externalFormUrl },
      );
      return response.data;
    } catch (error) {
      console.error("Error recording external GDPR consent:", error);
      throw error;
    }
  },

  // Get GDPR history for a candidate
  getCandidateGdprHistory: async (
    candidateId: number | string,
  ): Promise<unknown> => {
    try {
      const response = await api.get(
        `/recruiting/candidates/${candidateId}/gdpr/history`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting candidate GDPR history:", error);
      throw error;
    }
  },

  // Delete candidate data (admin only)
  deleteGdprData: async (
    candidateId: number | string,
    notes?: string,
  ): Promise<unknown> => {
    try {
      const response = await api.post(
        `/recruiting/candidates/${candidateId}/gdpr/delete-data`,
        { notes },
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting GDPR data:", error);
      throw error;
    }
  },

  // Get GDPR compliance report (admin only)
  getGdprReport: async (
    filters: Record<string, unknown> = {},
  ): Promise<unknown> => {
    try {
      const response = await api.get("/recruiting/gdpr/report", {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error("Error getting GDPR report:", error);
      throw error;
    }
  },

  // Get GDPR configuration
  getGdprConfig: async (): Promise<unknown> => {
    try {
      const response = await api.get("/recruiting/gdpr/config");
      return response.data;
    } catch (error) {
      console.error("Error getting GDPR config:", error);
      throw error;
    }
  },

  // Update GDPR configuration (admin only)
  setGdprConfig: async (config: Record<string, string>): Promise<unknown> => {
    try {
      const response = await api.post("/recruiting/gdpr/config", config);
      return response.data;
    } catch (error) {
      console.error("Error setting GDPR config:", error);
      throw error;
    }
  },

  // ===== End GDPR Consent Management =====

  // ===== Candidate Import Wizard =====

  // Upload and parse single CV
  uploadSingleCV: async (formData: FormData): Promise<any> => {
    try {
      const response = await api.post(
        "/candidate-import/upload-single",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading CV:", error);
      throw error;
    }
  },

  // Finalize candidate import (create candidate from parsed data)
  finalizeImport: async (data: any): Promise<any> => {
    try {
      const response = await api.post("/candidate-import/finalize", data);
      return response.data;
    } catch (error) {
      console.error("Error finalizing import:", error);
      throw error;
    }
  },

  // Finalize bulk candidate import (create multiple candidates)
  finalizeBulkImport: async (data: { candidates: any[] }): Promise<any> => {
    try {
      const response = await api.post("/candidate-import/finalize-bulk", data, {
        timeout: 300000, // 5 minutes for bulk creation
      });
      return response.data;
    } catch (error) {
      console.error("Error finalizing bulk import:", error);
      throw error;
    }
  },

  // ===== End Candidate Import Wizard =====

  // ===== Job Import =====

  // Upload and parse a job description document (PDF/DOC/DOCX)
  uploadJobDocument: async (
    formData: FormData,
  ): Promise<{
    success: boolean;
    data: Record<string, unknown> | null;
    method: string;
    error?: string;
    rawText?: string;
  }> => {
    try {
      const response = await api.post("/job-import/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading job document:", error);
      throw error;
    }
  },

  // Parse job from URL
  parseJobFromUrl: async (
    url: string,
  ): Promise<{
    success: boolean;
    data: Record<string, unknown> | null;
    method: string;
    error?: string;
    rawText?: string;
  }> => {
    try {
      const response = await api.post("/job-import/from-url", { url });
      return response.data;
    } catch (error) {
      console.error("Error parsing job from URL:", error);
      throw error;
    }
  },

  // ===== End Job Import =====

  // ===== Candidate Share Links =====

  async generateShareLink(candidateId: number): Promise<{ url: string }> {
    const response = await api.post(
      `/recruiting/candidates/${candidateId}/share-link`,
    );
    return response.data?.data;
  },

  // ===== Lifecycle Coordination (US1-US5) =====

  // US2: Bulk reject a candidate's other applications
  bulkRejectCandidateApplications: async (
    applicationIds: number[],
    reason: string,
    note?: string,
  ): Promise<{ rejected_count: number }> => {
    try {
      const response = await api.post(
        "/recruiting/applications/bulk-reject-for-candidate",
        {
          application_ids: applicationIds,
          reason,
          note,
        },
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("Error bulk rejecting candidate applications:", error);
      throw error;
    }
  },

  // US5: Get stale pipeline items
  getStaleItems: async (): Promise<{
    follow_up_due: Array<{
      id: number;
      first_name: string;
      last_name: string;
      main_skill?: string;
      follow_up_date: string;
      responsible_recruiter_name?: string;
    }>;
    stuck_candidates: Array<{
      id: number;
      first_name: string;
      last_name: string;
      main_skill?: string;
      status: string;
      days_in_status: number;
      responsible_recruiter_name?: string;
    }>;
    stale_applications: Array<{
      id: number;
      status: string;
      days_in_status: number;
      candidate_first_name: string;
      candidate_last_name: string;
      job_title: string;
    }>;
    counts: {
      follow_up_due: number;
      stuck_candidates: number;
      stale_applications: number;
    };
  }> => {
    try {
      const response = await api.get("/recruiting/stale-items");
      return response.data?.data || response.data;
    } catch (error) {
      console.error("Error getting stale items:", error);
      throw error;
    }
  },

  // ===== End Lifecycle Coordination =====
};

export const recruitingApi = recruitingAPI;
export default recruitingAPI;
